import { Controller } from "@hotwired/stimulus";


/**
 * Stimulus controller for user interface logic on survey questions.
 * Handles open-ended question display, answer state, and refresh failsafe.
 *
 * @class UserInterfaceController
 * @extends Controller
 */
export default class extends Controller {
  /**
   * Stimulus targets for DOM elements.
   * @type {string[]}
   */
  static targets = [
    'answer',
    'open',
    'openTitle',
    'loader',
    'submitButton',
    'error'
  ];

  /**
   * Values for answer state and waiting room logic.
   * @type {{answered: Boolean, lastWaitingRoomQuestion: Boolean}}
   */
  static values = {
    answered: {
      type: Boolean,
      default: false
    },
    lastWaitingRoomQuestion: {
      type: Boolean,
      default: false
    }
  };

  /**
   * Track starting position for transition verification.
   * @type {number|null}
   */
  startingPosition = null;

  /**
   * Called when the controller is connected to the DOM.
   * Sets up open-ended UI, multi-select validation, and triggers failsafe if already answered.
   */
  connect() {
    this.setOpen();
    // Firefox-specific autofill/radio memory guard:
    // Some Firefox versions incorrectly retain or auto-select the first radio in a required group
    // when the input "name" is a bare number (e.g., "4") and no prior answer exists. We defensively
    // clear any pre-checked state for unanswered questions before user interaction.
    try {
      if (!this.answeredValue) {
        // Only clear radios we own (custom-control-input & data flag) to avoid wiping legitimate restores.
        const strayChecked = Array.from(document.querySelectorAll('input[type="radio"].custom-control-input[data-firefox-autofill-guard="true"]:checked'));
        if (strayChecked.length > 0) {
          strayChecked.forEach(el => { el.checked = false; });
        }
      }
    } catch (e) { /* noop safeguard */ }
    this.setupMultiSelectValidation();
    this.setupTurboErrorHandler();
    if (this.answeredValue) {
      // Used by legacy code to prevent double-answering
      window.answeredAlready = true;
      this.setFailsafe();
    }
    // Note: WebSocket subscription is now handled by user_layout_controller.js
    // on the <body> element to survive Turbo Frame replacements
  }

  /**
   * Called when the controller is disconnected from the DOM.
   */
  disconnect() {
    this.removeTurboErrorHandler();
  }

  /**
   * Set up global Turbo fetch error handler for network failures.
   * This catches errors that occur before turbo:submit-end fires.
   */
  setupTurboErrorHandler() {
    this.boundFetchRequestError = this.handleTurboFetchError.bind(this);
    document.addEventListener('turbo:fetch-request-error', this.boundFetchRequestError);
  }

  /**
   * Remove the global Turbo error handler on disconnect.
   */
  removeTurboErrorHandler() {
    if (this.boundFetchRequestError) {
      document.removeEventListener('turbo:fetch-request-error', this.boundFetchRequestError);
    }
  }

  /**
   * Handle Turbo fetch errors (network failures, CORS issues, etc.)
   */
  handleTurboFetchError(event) {
    console.warn('Turbo fetch error:', event.detail?.error);
    this.handleSubmissionError(null);
  }

  /**
   * Setup real-time validation for multi-select questions
   */
  setupMultiSelectValidation() {
    const multiSelectInputs = document.querySelectorAll('.ACU_NON_EXCLUSIVE, .ACU_EXCLUSIVE');
    if (multiSelectInputs.length === 0) return;

    // Add change listeners to provide immediate feedback
    multiSelectInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.validateMultiSelect();
        this.clearFormErrors(); // Clear any previous submission errors
      });
    });
  }

  /**
   * Validate multi-select questions in real-time
   */
  validateMultiSelect() {
    const multiSelectInputs = document.querySelectorAll('.ACU_NON_EXCLUSIVE, .ACU_EXCLUSIVE');
    if (multiSelectInputs.length === 0) return true;

    const hasSelection = Array.from(multiSelectInputs).some(input => input.checked);

    if (this.hasSubmitButtonTarget) {
      // Enable/disable submit button based on selection
      this.submitButtonTarget.disabled = !hasSelection;

      // Visual feedback using toggle with force parameter
      this.submitButtonTarget.classList.toggle('btn-secondary', !hasSelection);
      this.submitButtonTarget.classList.toggle('btn-live', hasSelection);
    }

    return hasSelection;
  }

  /**
   * Clear form error containers
   */
  clearFormErrors() {
    const formErrors = document.getElementById('form-errors');
    if (formErrors) {
      formErrors.innerHTML = '';
    }

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = '';
      this.errorTarget.style.display = 'none';
    }
  }

  /**
   * Turbo submit start: show loader, disable submit button, and provide optimistic feedback.
   * Also sets global flag to signal to user_layout_controller that a submission is in progress,
   * allowing it to defer moderator-initiated redirects until the answer is submitted.
   */
  onSubmitStart() {
    try {
      // Signal that form submission is in progress (read by user_layout_controller)
      // This allows moderator-initiated redirects to be deferred until after submission
      window.acupollFormSubmitting = true;

      // Capture question position for transition verification
      const form = this.element;
      this.startingPosition = form ? parseInt(form.dataset.questionPosition, 10) : null;

      // Clear any prior error state
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = ''
        this.errorTarget.style.display = 'none'
      }

      // Clear form errors container
      const formErrors = document.getElementById('form-errors')
      if (formErrors) {
        formErrors.innerHTML = ''
      }

      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = true;
        this.submitButtonTarget.textContent = 'Submitting...';
      }
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.remove('d-none');
      }

      // Optimistic UI: Show progress feedback
      this.showOptimisticFeedback();
    } catch (e) {
      console.warn('UserInterface onSubmitStart error:', e);
      // no-op; keep UX resilient
    }
  }

  /**
   * Turbo submit end: handle response and update UI accordingly.
   * Clears the global submission flag. Pending redirects (deferred during
   * submission) are handled by user_layout_controller.handleTurboSubmitEnd().
   */
  onSubmitEnd(event) {
    try {
      // Clear the submission flag - form is no longer submitting
      window.acupollFormSubmitting = false;

      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.add('d-none');
      }

      // Reset submit button state
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false;
        this.submitButtonTarget.textContent = 'Next';
      }

      // Check if response was successful based on Turbo Stream response
      const response = event?.detail?.fetchResponse?.response;
      const isSuccess = response?.ok && response?.status === 200;

      if (!isSuccess) {
        // Handle both HTTP errors (response present) and network failures (response undefined)
        this.handleSubmissionError(response);
      } else {
        // On success, verify transition completed correctly (belt-and-suspenders for race conditions)
        this.verifyTransitionAfterDelay();
      }

      // Note: Pending redirect execution is handled by user_layout_controller.handleTurboSubmitEnd()
      // which listens for turbo:submit-end at the document level. This ensures a single source
      // of truth for redirect logic and proper cleanup of the survey-advanced-indicator.
    } catch (e) {
      console.warn('UserInterface onSubmitEnd error:', e);
      // Re-enable form on any error
      this.resetFormState();
    }
  }

  /**
   * Reset form to interactive state after any error.
   */
  resetFormState() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.textContent = 'Next';
    }
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('d-none');
    }
    const form = document.getElementById('user-interface');
    if (form) {
      form.style.opacity = '1';
      form.style.pointerEvents = 'auto';
    }
  }

  /**
   * Show optimistic feedback during submission
   */
  showOptimisticFeedback() {
    // Add a subtle visual indicator that submission is processing
    const form = document.getElementById('user-interface');
    if (form) {
      form.style.opacity = '0.8';
      form.style.pointerEvents = 'none';
    }
  }

  /**
   * Handle submission errors gracefully.
   * @param {Response|undefined} response - The fetch response, or undefined for network failures
   */
  handleSubmissionError(response) {
    let errorMessage = 'Submission failed. Please try again.';

    if (!response) {
      // Network failure - no response received
      errorMessage = 'Connection lost. Your answers are preserved - please check your connection and try again.';
    } else if (response.status === 422) {
      errorMessage = 'Please check your answers and try again.';
    } else if (response.status === 401 || response.status === 403) {
      errorMessage = 'Your session has expired. Please refresh the page to log in again.';
    } else if (response.status >= 500) {
      errorMessage = 'Server error. Your answers are preserved - please try again in a moment.';
    } else if (response.status === 408 || response.status === 0) {
      errorMessage = 'Request timed out. Please check your connection and try again.';
    }

    // Show error in the error target
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = errorMessage;
      this.errorTarget.style.display = 'block';
    }

    // Also show in form errors container if it exists
    const formErrors = document.getElementById('form-errors');
    if (formErrors) {
      formErrors.innerHTML = `<div class="alert alert-danger">${errorMessage}</div>`;
    }

    // Reset form to interactive state
    this.resetFormState();
  }

  /**
   * Verify transition completed correctly after a short delay.
   * Detects race conditions where hold overlay shows incorrectly.
   * If server reports different position than displayed, triggers reload.
   */
  verifyTransitionAfterDelay() {
    // Wait for DOM to settle after Turbo Stream replacement
    setTimeout(() => this.verifyTransition(), 500);
  }

  /**
   * Check if hold overlay is showing and verify position with server.
   */
  verifyTransition() {
    try {
      // Check if hold overlay is now visible
      const holdOverlay = document.querySelector('.hold-overlay');
      if (!holdOverlay) {
        // No hold overlay - transition completed normally or new question showing
        return;
      }

      // Get the position from the hold overlay
      const overlayPosition = parseInt(holdOverlay.dataset.questionPosition, 10);

      // Only verify if we captured a starting position and overlay is at same position
      if (!this.startingPosition || overlayPosition !== this.startingPosition) {
        // Different position means we did advance, just showing hold for next question
        return;
      }

      // Hold overlay showing at same position we submitted from - verify with server
      console.log('UserInterface: Verifying transition, overlay at position', overlayPosition);

      fetch('/user_interface/status', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data || typeof data.current_question === 'undefined') return;

          const serverPosition = parseInt(data.current_question, 10);
          if (serverPosition && serverPosition !== overlayPosition) {
            // Server says we're at a different position - race condition occurred
            console.log('UserInterface: Position mismatch detected, server:', serverPosition, 'overlay:', overlayPosition);
            this.triggerReload();
          }
        })
        .catch(err => {
          console.warn('UserInterface: Transition verification failed', err);
          // On error, don't reload - let normal polling handle it
        });
    } catch (e) {
      console.warn('UserInterface: verifyTransition error', e);
    }
  }

  /**
   * Trigger page/frame reload to correct stale state.
   */
  triggerReload() {
    // Use Turbo.visit for seamless navigation
    if (window.Turbo) {
      Turbo.visit('/user_interface/new?t=' + Date.now(), { action: 'replace' });
    } else {
      window.location.href = '/user_interface/new?t=' + Date.now();
    }
  }

  /**
   * Sets failsafe timers to refresh the page and show the refresh button after delays.
   * - Refreshes the page after 3 minutes (basic) or 4 minutes (last waiting room question).
   * - Shows the refresh button at 4:15 minutes (255000 ms) for fallback.
   */
  setFailsafe() {
    let refresh_time = 180000; // 3 minutes default
    if (this.lastWaitingRoomQuestionValue) {
      refresh_time = 240000; // 4 minutes for last waiting room question
    }
    setTimeout(() => {
      window.location.reload();
    }, refresh_time);

    // Show the refresh button at 4:15 minutes (255000 ms) for fallback
    setTimeout(() => {
      this.enableRefreshNow();
    }, 255000);
  }

  /**
   * Shows the "refresh now" button and hides the "refresh later" button.
   */
  enableRefreshNow() {
    document.getElementById('refresh-later').classList.add('d-none');
    document.getElementById('refresh-now').classList.remove('d-none');
    // If using jQuery, could use .hide() instead
  }

  /**
   * Sets up the open-ended question UI if an answer is selected.
   * Focuses the textarea for open-ended input.
   */
  setOpen() {
    if (!this.hasOpenTarget) {
      return;
    }
    const checked = this.answerTargets.find(answer => answer.checked);
    if (checked != null) {
      // Set the open question title to match the selected answer's label
      this.openTitleTarget.innerHTML = document.querySelector(`label[for="${checked.id}"]`).innerText;
      this.openTarget.classList.remove('d-none');
      this.openTarget.querySelector('textarea').focus();
    } else {
      this.openTarget.classList.add('d-none');
    }
  }
}
